import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  CheckCircle,
  Circle,
  Search,
  ExternalLink
  // Activity, Zap, TrendingUp — used by commented ecosystem/benchmark section
} from "lucide-react";
import {
  getProviderCategories,
  getProviders
} from "../services/providerService";
import { useProviderLogos } from "../hooks/useProviderLogo";
import { canAuthorInHouse } from "../services/authService";
import InHouseTiles from "./inHouseBuilder/InHouseTiles";

const IN_HOUSE = "In-House Integrations";

export default function Dashboard() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const [activeFilter, setActiveFilter] = useState(
    tabParam === "in-house" ? IN_HOUSE : "All Integrations"
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [filterCategories, setFilterCategories] = useState([
    "All Integrations"
  ]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [categoryMap, setCategoryMap] = useState({}); // Store code->name mapping from API
  const [integrations, setIntegrations] = useState([]);
  const [integrationsLoading, setIntegrationsLoading] = useState(true);
  // Ids the builder tiles already show, so the same integration isn't listed twice.
  const [builderIds, setBuilderIds] = useState(() => new Set());
  // Until the builder tiles have loaded, the list below can't tell which cards they'll show,
  // so it waits instead of flashing duplicates.
  const [builderReady, setBuilderReady] = useState(false);

  // Follow ?tab= when the URL changes without a remount (back/forward, a link to the tab).
  useEffect(() => {
    if (tabParam === "in-house") setActiveFilter(IN_HOUSE);
    else setActiveFilter((f) => (f === IN_HOUSE ? "All Integrations" : f));
  }, [tabParam]);

  /** Tab clicks write ?tab= too, so a reload or a shared link opens the same tab. */
  const selectFilter = (category) => {
    setActiveFilter(category);
    setSearchParams(
      (params) => {
        const next = new URLSearchParams(params);
        if (category === IN_HOUSE) next.set("tab", "in-house");
        else next.delete("tab");
        return next;
      },
      { replace: true }
    );
  };

  // Fetch provider logos using custom hook
  const providerLogos = useProviderLogos(integrations);

  // Fetch provider categories on component mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setCategoriesLoading(true);
        const response = await getProviderCategories();

        if (response.success && response.data) {
          // Create category name array for filters
          const categories = [
            "All Integrations",
            ...response.data.map((cat) => cat.name)
          ];
          setFilterCategories(categories);

          // Create code->name mapping for provider categorization
          const mapping = {};
          response.data.forEach((cat) => {
            mapping[cat.code] = cat.name;
          });
          setCategoryMap(mapping);
        }
      } catch (error) {
        console.error("Error loading categories:", error);
      } finally {
        setCategoriesLoading(false);
      }
    };

    fetchCategories();
  }, []);

  // Fetch providers after categories are loaded
  useEffect(() => {
    // Only fetch providers after categories are loaded
    if (Object.keys(categoryMap).length === 0) {
      return;
    }

    const fetchProviders = async () => {
      try {
        setIntegrationsLoading(true);
        const response = await getProviders({ offset: 0, limit: 100 });

        if (response.success && response.data) {
          // Transform API data to match the UI format
          const transformedProviders = response.data.map((provider) => ({
            id: provider.provider_id,
            slug:
              provider.ui_name?.toLowerCase().replace(/\s+/g, "_") ||
              provider.provider_name?.toLowerCase().replace(/\s+/g, "_"),
            name: provider.ui_name || provider.provider_name,
            description:
              provider.description ||
              (provider.documentation_url
                ? `View documentation at ${provider.documentation_url}`
                : `${provider.provider_name} integration`),
            status: provider.status || "Available",
            category:
              categoryMap[provider.provider_type] || provider.provider_type,
            tag: categoryMap[provider.provider_type] || provider.provider_type,
            documentationUrl: provider.documentation_url,
            providerType: provider.provider_type,
            // Preserve raw API values for navigation
            uptime_percentage: provider.uptime_percentage,
            total_requests: provider.total_requests,
            successful_requests: provider.successful_requests,
            // Add formatted metrics for display
            stats: {
              requests: provider.total_requests?.toLocaleString() || "0",
              uptime:
                provider.uptime_percentage !== null &&
                provider.uptime_percentage !== undefined
                  ? `${provider.uptime_percentage}%`
                  : "N/A",
              synced:
                provider.successful_requests !== null &&
                provider.successful_requests !== undefined
                  ? `${provider.successful_requests} / ${provider.total_requests || 0}`
                  : "N/A"
            }
          }));

          setIntegrations(transformedProviders);
        }
      } catch (error) {
        console.error("Error loading providers:", error);
      } finally {
        setIntegrationsLoading(false);
      }
    };

    fetchProviders();
  }, [categoryMap]);

  /*
  const summaryStats = [
    {
      title: "Connected APIs",
      value: "5",
      icon: CheckCircle,
      gradient: "from-green-50 to-emerald-50",
      bgColor: "bg-green-100",
      iconColor: "text-green-600",
      circleColor: "bg-green-200",
      subtext: "All systems operational",
      subtextColor: "text-green-600",
      showArrow: true
    },
    {
      title: "Available",
      value: "3",
      icon: Circle,
      gradient: "from-purple-50 to-violet-50",
      bgColor: "bg-purple-100",
      iconColor: "text-purple-600",
      circleColor: "bg-purple-200",
      subtext: "Ready to connect",
      subtextColor: "text-gray-600",
      showArrow: false
    },
    {
      title: "API Requests",
      value: "46,811",
      icon: Activity,
      gradient: "from-blue-50 to-cyan-50",
      bgColor: "bg-blue-100",
      iconColor: "text-blue-600",
      circleColor: "bg-blue-200",
      subtext: "Last 24 hours",
      subtextColor: "text-gray-600",
      showArrow: false
    },
    {
      title: "Avg Uptime",
      value: "99.8%",
      icon: Zap,
      gradient: "from-amber-50 to-yellow-50",
      bgColor: "bg-amber-100",
      iconColor: "text-amber-600",
      circleColor: "bg-amber-200",
      subtext: "Enterprise SLA",
      subtextColor: "text-gray-600",
      showArrow: false
    }
  ];

  const benchmarkData = [
    {
      category: "Risk Intelligence APIs",
      yourUsage: 21374,
      industryAvg: 15200,
      percentage: 82
    },
    {
      category: "Property Data APIs",
      yourUsage: 15234,
      industryAvg: 18500,
      percentage: 58
    },
    {
      category: "Communication APIs",
      yourUsage: 3421,
      industryAvg: 8900,
      percentage: 32
    }
  ];

  const benchmarkInsight = {
    text: "You're leveraging Risk Intelligence APIs 40% more than industry peers. Consider exploring Communication APIs to enhance your workflow automation."
  };
  */

  // Hardcoded filter categories removed - now fetched from API via useEffect
  // Integrations are now fetched from API via useEffect

  // Only org admins can author, so only they get the builder tiles on this tab.
  const authoring = activeFilter === IN_HOUSE && canAuthorInHouse();

  // Leaving the tab forgets what the tiles showed; coming back waits for them to load again
  // instead of filtering the list with ids that may be out of date.
  useEffect(() => {
    if (!authoring) {
      setBuilderIds(new Set());
      setBuilderReady(false);
    }
  }, [authoring]);

  const filteredIntegrations = integrations.filter((integration) => {
    const matchesFilter =
      activeFilter === "All Integrations" ||
      integration.category === activeFilter;
    const matchesSearch =
      searchQuery === "" ||
      integration.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (integration.description || "").toLowerCase().includes(searchQuery.toLowerCase());
    const shownByBuilder = authoring && builderIds.has(integration.id);
    return matchesFilter && matchesSearch && !shownByBuilder;
  });

  return (
    <div className="flex-1 overflow-auto bg-[#FCFCFC]">
      <div className="max-w-[1400px] mx-auto p-8">
        {/* Ecosystem overview + industry benchmark (hardcoded) — hidden until real API data */}
        {/*
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-3">
              Your Connected Ecosystem
            </h2>
            <p className="text-gray-600 mb-6">
              Real-time data enrichment powering Bench underwriting and Studio
              workflows. Enterprise-grade APIs with 99.9% uptime SLA.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {summaryStats.map((stat, index) => {
                const Icon = stat.icon;
                return (
                  <div
                    key={index}
                    className={`relative bg-gradient-to-br ${stat.gradient} rounded-2xl p-6 border border-gray-200 overflow-hidden`}
                  >
                    <div
                      className={`absolute -top-8 -right-8 w-24 h-24 ${stat.circleColor} rounded-full opacity-30`}
                    ></div>

                    <div
                      className={`relative ${stat.bgColor} w-12 h-12 rounded-xl flex items-center justify-center mb-4`}
                    >
                      <Icon className={`w-6 h-6 ${stat.iconColor}`} />
                    </div>
                    <h3 className="relative text-gray-600 text-sm font-medium mb-2">
                      {stat.title}
                    </h3>
                    <p className="relative text-4xl font-bold text-gray-900 mb-2">
                      {stat.value}
                    </p>
                    <p
                      className={`relative text-xs font-medium ${stat.subtextColor} flex items-center gap-1`}
                    >
                      {stat.showArrow && <TrendingUp className="w-4 h-4" />}
                      {stat.subtext}
                    </p>
                  </div>
                );
              })}
            </div>

            <div className="bg-white rounded-2xl p-8 mb-8 border border-gray-200">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    Industry Benchmark
                  </h3>
                  <p className="text-gray-600">
                    Compare your API consumption with industry averages
                  </p>
                </div>
                <div className="flex items-center gap-2 text-gray-700">
                  <TrendingUp className="w-5 h-5" />
                  <span className="font-medium">Above Average</span>
                </div>
              </div>

              <div className="space-y-6 mb-6">
                {benchmarkData.map((benchmark, index) => (
                  <div key={index}>
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-base font-semibold text-gray-900">
                        {benchmark.category}
                      </h4>
                      <span className="text-sm text-gray-600">
                        Your usage:{" "}
                        <span className="font-semibold text-gray-900">
                          {benchmark.yourUsage.toLocaleString()}
                        </span>{" "}
                        / Industry avg: {benchmark.industryAvg.toLocaleString()}
                      </span>
                    </div>
                    <div className="relative h-3 bg-blue-100 rounded-full overflow-hidden">
                      <div
                        className="absolute top-0 left-0 h-full bg-blue-600 rounded-full transition-all duration-500"
                        style={{ width: `${benchmark.percentage}%` }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <p className="text-sm text-gray-700">
                  <span className="font-semibold text-blue-900">Insight:</span>{" "}
                  {benchmarkInsight.text}
                </p>
              </div>
            </div>

            <div className="border-b border-gray-300"></div>
          </div>
        */}

        {/* Search Bar */}
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-600 w-5 h-5" />
            <input
              type="text"
              placeholder="Search integrations by name, category, or capability..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder:text-gray-600 text-sm"
            />
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="mb-8 overflow-x-auto scrollbar-hide">
          {categoriesLoading ? (
            <div className="flex space-x-2 min-w-max">
              {/* Loading skeleton for filter tabs */}
              {[1, 2, 3, 4, 5].map((i) => (
                <div
                  key={i}
                  className="px-[21px] py-[11px] rounded-full bg-gray-200 animate-pulse h-10 w-32"
                ></div>
              ))}
            </div>
          ) : (
            <div className="flex space-x-2 min-w-max">
              {filterCategories.map((category) => (
                <button
                  key={category}
                  onClick={() => selectFilter(category)}
                  className={`px-[21px] py-[11px] rounded-full font-medium text-sm transition-all whitespace-nowrap ${
                    activeFilter === category
                      ? "bg-blue-600 text-white shadow-md"
                      : "bg-white text-gray-700 hover:bg-gray-100 border border-gray-200"
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Integrations Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 xl:grid-cols-3 gap-6">
          {/* Admins also get the builder tiles; existing in-house integrations stay listed below. */}
          {authoring && (
            <InHouseTiles
              searchQuery={searchQuery}
              onLoaded={(ids) => {
                setBuilderIds(new Set(ids));
                setBuilderReady(true);
              }}
            />
          )}
          {integrationsLoading || (authoring && !builderReady) ? (
            // Loading skeleton (the builder tiles draw their own on this tab)
            authoring ? null : Array.from({ length: 6 }).map((_, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl border border-gray-200 px-6 pt-6 pb-10 animate-pulse"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex flex-col items-start">
                    <div className="w-14 h-14 bg-gray-200 rounded-xl mb-2"></div>
                    <div className="h-6 w-32 bg-gray-200 rounded"></div>
                  </div>
                  <div className="h-6 w-20 bg-gray-200 rounded-md"></div>
                </div>
                <div className="h-10 bg-gray-200 rounded mb-6"></div>
                <div className="border-b border-gray-300 mb-6"></div>
              </div>
            ))
          ) : filteredIntegrations.length === 0 ? (
            // Empty state (the builder tiles already fill the grid for admins)
            authoring ? null : (
            <div className="col-span-full text-center py-12">
              <Circle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                No integrations found
              </h3>
              <p className="text-gray-600">
                {searchQuery
                  ? "Try adjusting your search or filters"
                  : "Get started by connecting your first integration"}
              </p>
            </div>
            )
          ) : (
            filteredIntegrations.map((integration) => (
              <div
                key={integration.id}
                onClick={() =>
                  navigate(`/integration/${integration.slug}`, {
                    state: {
                      providerId: integration.id,
                      providerData: integration
                    }
                  })
                }
                className="flex min-w-0 flex-col bg-white rounded-2xl border border-gray-200 px-6 pt-6 pb-10 hover:shadow-lg transition-shadow cursor-pointer"
              >
                {/* Header */}
                <div className="flex items-start justify-between gap-3 mb-4">
                  <div className="flex min-w-0 flex-col items-start">
                    {providerLogos[integration.id] ? (
                      <img
                        src={providerLogos[integration.id]}
                        alt={`${integration.name} logo`}
                        className="w-24 h-24 rounded-xl object-contain mb-2"
                      />
                    ) : (
                      <div className="w-24 h-24 rounded-xl bg-gray-100 flex items-center justify-center mb-2">
                        <span className="text-lg font-bold text-gray-400">
                          {integration.name?.charAt(0).toUpperCase() || "?"}
                        </span>
                      </div>
                    )}
                    <div>
                      <h3 title={integration.name} className="line-clamp-2 text-xl font-bold text-gray-900 [overflow-wrap:anywhere]">
                        {integration.name}
                      </h3>
                    </div>
                  </div>
                  <span
                    className={`flex shrink-0 items-center space-x-1 px-[13px] py-[5px] rounded-md text-xs font-medium ${
                      integration.status === "Connected"
                        ? "bg-green-600 text-white"
                        : "bg-white text-gray-700 border border-gray-200"
                    }`}
                  >
                    {integration.status === "Connected" && (
                      <CheckCircle className="w-3 h-3" />
                    )}
                    <span>{integration.status}</span>
                  </span>
                </div>

                {/* Description */}
                <p className="line-clamp-3 break-words text-gray-600 text-sm mb-6 min-h-[40px]">
                  {integration.description}
                </p>

                <div className="border-b border-gray-300 mb-6"></div>

                {/* Stats */}
                {integration.stats && (
                  <div className="grid grid-cols-3 gap-4 pb-6">
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Requests</p>
                      <p className="text-lg font-bold text-gray-900">
                        {integration.stats.requests}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Uptime</p>
                      <p className="text-lg font-bold text-green-600">
                        {integration.stats.uptime}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Synced</p>
                      <p className="text-sm font-medium text-gray-700">
                        {integration.stats.synced}
                      </p>
                    </div>
                  </div>
                )}

                {/* Footer — pinned to the bottom so footers line up across a row */}
                <div className="mt-auto flex items-center justify-between">
                  <span className="text-xs font-medium text-gray-600 bg-gray-100 px-3 py-1 rounded-lg">
                    {integration.tag}
                  </span>
                  <button
                    className={`flex items-center space-x-1 px-[13px] py-[5px] rounded-lg font-medium text-sm border border-gray-200 transition-colors ${
                      integration.status === "Connected"
                        ? "bg-white text-gray-700 hover:bg-gray-200"
                        : "bg-blue-600 text-white hover:bg-blue-700"
                    }`}
                  >
                    <span>
                      {integration.status === "Connected"
                        ? "Configure"
                        : "Connect"}
                    </span>
                    <ExternalLink className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
